"use client";
// Imports
import usePersistStore from "@/lib/usePersistStore";
import { useMyShowStore } from "@/lib/persistState";
import { normaliseMyShowSlugs } from "@/lib/myShow";
import { Drawer, MobileDrawer, MyShowDrawerContent } from "@/components";
import { useEffect, useState } from "react";

function getValidatedEventCount(savedSlugs, events) {
  const uniqueSavedSlugs = [...new Set(normaliseMyShowSlugs(savedSlugs))];

  return events.reduce((total, event) => {
    const validSubEvents = uniqueSavedSlugs.filter((slug) => {
      const match = slug.match(/-sub-(\d+)$/);
      if (!match || slug.replace(/-sub-\d+$/, "") !== event.slug) return false;

      return Boolean(event.days?.[Number(match[1])]);
    });

    if (validSubEvents.length > 0) return total + validSubEvents.length;

    return total + (uniqueSavedSlugs.includes(event.slug) ? 1 : 0);
  }, 0);
}

// Ui Element
export function UIElement({ isMobile, slug, user, showDates }) {
  const [totalAmount, setTotalAmount] = useState(0);

  const myShowStore = usePersistStore(useMyShowStore, (state) => state);
  const myShowbags = myShowStore?.myShowbags || [];
  const myRides = myShowStore?.myRides || [];
  const myEvents = myShowStore?.myEvents || [];
  const myFood = myShowStore?.myFood || [];

  useEffect(() => {
    const validMyShowbags = normaliseMyShowSlugs(myShowbags);
    const validMyRides = normaliseMyShowSlugs(myRides);
    const validMyEvents = normaliseMyShowSlugs(myEvents);
    const validMyFood = normaliseMyShowSlugs(myFood);
    const lookupSlugs = [
      ...validMyShowbags,
      ...validMyRides,
      ...validMyEvents.map((slug) => slug.replace(/-sub-\d+$/, "")),
      ...validMyFood,
    ];
    const uniqueLookupSlugs = [...new Set(lookupSlugs)];

    if (uniqueLookupSlugs.length === 0) {
      setTotalAmount(0);
      return;
    }

    let cancelled = false;
    setTotalAmount(0);

    const fetchValidatedTotal = async () => {
      try {
        const response = await fetch("/api/fetch-myshow-data", {
          method: "POST",
          body: JSON.stringify(uniqueLookupSlugs),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();

        if (cancelled) return;

        setTotalAmount(
          getValidatedEventCount(validMyEvents, data?.events?.data || []) +
            (data?.showbags?.data?.length || 0) +
            (data?.rides?.data?.length || 0) +
            (data?.food?.data?.length || 0)
        );
      } catch (error) {
        if (!cancelled) {
          setTotalAmount(0);
          console.error("Error validating My Show count:", error);
        }
      }
    };

    fetchValidatedTotal();

    return () => {
      cancelled = true;
    };
  }, [myShowbags, myRides, myEvents, myFood]);

  if (!myShowStore && !isMobile)
    return (
      <p className="cursor-not-allowed text-brand-nav-accent level-4 normal-case">
        My Show
      </p>
    );
  if (!myShowStore && isMobile)
    return (
      <div className="cursor-not-allowed text-brand-nav-accent py-4">
        <span className="font-heading font-heading-sentence">My Show</span>
      </div>
    );

  if (isMobile) {
    return (
      <MobileDrawer
        user={user}
        isMobile={isMobile}
        className="bg-background"
        direction="bottom"
        title="My Show"
        buttonTitle="My Show"
        totalAmount={totalAmount}
      >
        <MyShowDrawerContent
          showDates={showDates}
          myShowStore={myShowStore}
          user={user}
        />
      </MobileDrawer>
    );
  }

  return (
    <Drawer
      user={user}
      isMobile={isMobile}
      className="bg-background"
      direction="bottom"
      title="My Show"
      buttonTitle="My Show"
      totalAmount={totalAmount}
    >
      <MyShowDrawerContent
        showDates={showDates}
        myShowStore={myShowStore}
        user={user}
      />
    </Drawer>
  );
}
